import React from "react";
import JobApplicationsService from "../../services/JobApplicationsService";
import JobApplicationEntity from "../../types/jobApplicationEntity.type";
import EmptyJobApplicationsView from "./EmptyJobApplicationsView";
import JobApplicationsList from "./JobApplicationsList";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

type Props = {
  userId?: number | null;
  fullName?: string | null;
};

type State = {
  jobApplicationEntity: JobApplicationEntity[];
  editingJobApplication: JobApplicationEntity | null;
  errorMessage?: string;
};

export default class JobApplicationsView extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      jobApplicationEntity: [],
      editingJobApplication: null,
      errorMessage: undefined,
    };
  }

  componentDidMount() {
    if (this.props.userId != null && this.props.userId > 0) {
      JobApplicationsService.getUserJobApplications(this.props.userId)
        .then((response) => {
          console.log("API response (getUserJobApplications):", response.data);
          const applications = Array.isArray(response.data) ? response.data : [];
          this.setState({ jobApplicationEntity: applications, errorMessage: undefined });
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to fetch user job applications";
          console.error("Fetch error (getUserJobApplications):", errorMsg);
          this.setState({ jobApplicationEntity: [], errorMessage: errorMsg });
        });
    } else {
      JobApplicationsService.getJobApplicationsBoard()
        .then((response) => {
          console.log("API response (getJobApplicationsBoard):", response.data);
          const applications = Array.isArray(response.data) ? response.data : [];
          this.setState({ jobApplicationEntity: applications, errorMessage: undefined });
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to fetch job applications board";
          console.error("Fetch error (getJobApplicationsBoard):", errorMsg);
          this.setState({ jobApplicationEntity: [], errorMessage: errorMsg });
        });
    }
  }

  handleEdit = (jobApplication: JobApplicationEntity) => {
    console.log("Editing job application:", jobApplication);
    this.setState({ editingJobApplication: jobApplication });
  };

  handleDelete = (jobApplicationId: number) => {
    if (window.confirm("Are you sure you want to delete this job application?")) {
      JobApplicationsService.deleteJobApplication(jobApplicationId)
        .then(() => {
          console.log("Deleted job application ID:", jobApplicationId);
          this.setState((prevState) => ({
            jobApplicationEntity: prevState.jobApplicationEntity.filter((j) => j.id !== jobApplicationId),
            errorMessage: undefined,
          }));
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || error.message || "Failed to delete job application";
          console.error("Delete error:", errorMsg);
          this.setState({ errorMessage: errorMsg });
        });
    }
  };

  handleSaveJobApplication = (updatedJobApplication: JobApplicationEntity) => {
    console.log("Saving job application:", updatedJobApplication);
    JobApplicationsService.updateJobApplication(updatedJobApplication)
      .then(() => {
        this.setState((prevState) => ({
          jobApplicationEntity: prevState.jobApplicationEntity.map((j) =>
            j.id === updatedJobApplication.id ? updatedJobApplication : j
          ),
          editingJobApplication: null,
          errorMessage: undefined,
        }));
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.message || error.message || "Failed to update job application";
        console.error("Update error:", errorMsg);
        this.setState({ errorMessage: errorMsg });
      });
  };

  render() {
    let view = null;

    console.log("jobApplicationEntity:", this.state.jobApplicationEntity);
    console.log("editingJobApplication:", this.state.editingJobApplication);

    if (Array.isArray(this.state.jobApplicationEntity) && this.state.jobApplicationEntity.length > 0) {
      view = (
        <JobApplicationsList
          key={this.props.userId}
          listItems={this.state.jobApplicationEntity}
          onEdit={this.handleEdit}
          onDelete={this.handleDelete}
        />
      );
    } else {
      view = <EmptyJobApplicationsView key={this.props.userId} fullName={this.props.fullName} />;
    }

    return (
      <div className="container">
        {this.state.errorMessage && (
          <div className="alert alert-danger">{this.state.errorMessage}</div>
        )}
        {view}
        {this.state.editingJobApplication && (
          <>
            <div
              className="modal-backdrop-custom"
              style={modalBackdropStyle}
              onClick={() => this.setState({ editingJobApplication: null })}
            ></div>
            <EditJobApplicationModal
              jobApplication={this.state.editingJobApplication}
              onSave={this.handleSaveJobApplication}
              onCancel={() => this.setState({ editingJobApplication: null })}
            />
          </>
        )}
      </div>
    );
  }
}

const modalBackdropStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  zIndex: 1000,
};

interface EditJobApplicationModalProps {
  jobApplication: JobApplicationEntity;
  onSave: (updatedJobApplication: JobApplicationEntity) => void;
  onCancel: () => void;
}

const EditJobApplicationModal: React.FC<EditJobApplicationModalProps> = ({
  jobApplication,
  onSave,
  onCancel,
}) => {
  console.log("Rendering EditJobApplicationModal with jobApplication:", jobApplication);

  const validationSchema = Yup.object({
    job: Yup.number().required("Job ID is required"),
    userId: Yup.number().required("Job Poster ID is required"),
    approved: Yup.boolean().nullable(),
  });

  return (
    <div className="modal-custom" style={modalStyle}>
      <div style={{ position: "relative" }}>
        <h2>Edit Job Application</h2>
        <button
          type="button"
          className="btn-close-custom"
          onClick={onCancel}
          style={{ position: "absolute", top: "10px", right: "10px", fontSize: "1.2em" }}
        >
          ×
        </button>
        <Formik
          initialValues={{
            job: jobApplication.job || 0,
            userId: jobApplication.userId || 0,
            approved: jobApplication.approved || false,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => onSave({ ...values, id: jobApplication.id })}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="form-group">
                <label>Job ID</label>
                <Field name="job" type="number" className="form-control" />
                <ErrorMessage name="job" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Job Poster ID</label>
                <Field name="userId" type="number" className="form-control" />
                <ErrorMessage name="userId" component="div" className="text-danger" />
              </div>
              <div className="form-group">
                <label>Approved</label>
                <Field name="approved" type="checkbox" className="form-control" />
                <ErrorMessage name="approved" component="div" className="text-danger" />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

const modalStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  padding: "20px",
  zIndex: 1100,
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  maxWidth: "600px",
  width: "90%",
  minWidth: "300px",
  minHeight: "200px",
  maxHeight: "80vh",
  overflowY: "auto",
  display: "block",
  visibility: "visible",
};
